import { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api.js';
import { setTimeZone } from './timezone.js';

/* The session scope the backend derived from the WHMCS SSO token.

   This is a convenience for the UI only — it decides what to *render*, never
   what a user may *reach*. Every request is re-scoped server-side against the
   session cookie, so tampering here gets you a different-looking sidebar and
   nothing else.

   Note what isn't here: which PBX platform serves this customer. The backend
   knows; the portal doesn't need to, and the customer certainly doesn't. */

export const ROLES = {
  TENANT_ADMIN: 'tenant_admin',
  EXTENSION_USER: 'extension_user',
};

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', session: null, error: null });

  useEffect(() => {
    let cancelled = false;

    api
      .get('/session/me')
      .then(({ session }) => {
        if (cancelled) return;
        // Show times and compute "today" in the tenant's timezone. A null just
        // leaves the browser timezone as the fallback. Set before the app
        // renders so the very first formatted time is already correct.
        setTimeZone(session.timezone);
        setState({
          status: 'ready',
          session: {
            clientId: session.client_id,
            // WHMCS holds the trading name; until we fetch it, the tenant is
            // the only label we have.
            company: session.company ?? session.tenant,
            tenant: session.tenant,
            role: session.role,
            roleLabel: session.role_label,
            extension: session.extension,
            timezone: session.timezone ?? null,
          },
          error: null,
        });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'signed-out', session: null, error: err });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'loading') {
    return <SplashMessage title="Signing you in…" />;
  }

  if (state.status === 'signed-out') {
    // The portal has no login of its own — the only way in is the WHMCS SSO
    // handover, so point the visitor at their client area.
    return (
      <SplashMessage
        title="Please sign in from your client area"
        body="The phone portal opens from your Airbytes client area. Sign in there, then open the phone system."
        action={WHMCS_CLIENT_AREA
          ? { href: WHMCS_CLIENT_AREA, label: 'Go to your client area' }
          : null}
      />
    );
  }

  return <SessionContext.Provider value={state.session}>{children}</SessionContext.Provider>;
}

// Where "Go to your client area" points. Set VITE_WHMCS_URL at build time.
const WHMCS_CLIENT_AREA = import.meta.env.VITE_WHMCS_URL || '';

function SplashMessage({ title, body, action }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
          {title}
        </div>
        {body && <p style={{ color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.5 }}>{body}</p>}
        {action && (
          <a
            href={action.href}
            className="btn btn--primary"
            style={{ marginTop: 16, textDecoration: 'none', display: 'inline-flex' }}
          >
            {action.label}
          </a>
        )}
      </div>
    </div>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside a SessionProvider');
  return ctx;
}

export function useIsTenantAdmin() {
  return useSession().role === ROLES.TENANT_ADMIN;
}

/* Words that are never part of how a business is initialised. Without this
   "HOUSE OF FLAGS LTD" reads as "HO", which looks like a typo rather than a
   monogram; skipping the connector gives "HF". */
const CONNECTORS = new Set(['of', 'and', 'the', 'for', 'at', 'in', 'on', 'a']);

/** Initials for the sidebar avatar — "Marsh & Wells Ltd" → "MW". */
export function initialsOf(name) {
  const words = (name || '?')
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w));

  // Drop connectors, but only when something is left to initialise: a company
  // genuinely called "The A" should still get a monogram.
  const meaningful = words.filter((w) => !CONNECTORS.has(w.toLowerCase()));

  return (meaningful.length > 0 ? meaningful : words)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || '?';
}
